import type { ProfileAutopayHistory, ProfileMembershipHistory } from '@/lib/contacts/types'
import { displayDate, displayDateTime } from '@/lib/dates'
import { membershipRenewalDueLabel } from '@/pages/PersonMembershipDisplay'

export function MembershipHistory({
  history,
  autopayHistory,
  timeZone = 'UTC',
}: {
  history: ProfileMembershipHistory[]
  autopayHistory: ProfileAutopayHistory[]
  timeZone?: string
}) {
  const entries = combinedHistory(history, autopayHistory)
  if (entries.length === 0) return null

  return (
    <section className="px-4 py-3">
      <h3 className="text-body font-semibold text-foreground">Recent activity</h3>
      <ul className="flex flex-col gap-3 text-body-sm text-muted-foreground">
        {entries.map((entry) => (
          <li
            key={`${entry.kind}-${entry.value.id}`}
            className="flex flex-wrap items-baseline gap-x-2 gap-y-1"
          >
            <span className="text-foreground">{historyDescription(entry)}</span>
            <span className="break-words text-pewter sm:whitespace-nowrap">
              {[entry.value.actor, displayDateTime(entry.value.occurred_at, timeZone)]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

type CombinedHistory =
  | { kind: 'membership'; value: ProfileMembershipHistory }
  | { kind: 'autopay'; value: ProfileAutopayHistory }

function combinedHistory(
  membership: ProfileMembershipHistory[],
  autopay: ProfileAutopayHistory[],
): CombinedHistory[] {
  return [
    ...membership.map((value): CombinedHistory => ({ kind: 'membership', value })),
    ...autopay.map((value): CombinedHistory => ({ kind: 'autopay', value })),
  ].sort((left, right) => right.value.occurred_at.localeCompare(left.value.occurred_at))
}

function historyDescription(entry: CombinedHistory) {
  if (entry.kind === 'membership') return membershipHistoryDescription(entry.value)

  if (entry.value.surface) return `${entry.value.event} from ${entry.value.surface}`
  if (entry.value.reason) return `${entry.value.event} · ${entry.value.reason}`
  return entry.value.event
}

function membershipHistoryDescription(entry: ProfileMembershipHistory) {
  if (entry.event === 'Renewed' && entry.renewal_due_on_after) {
    return withPaymentDetail(
      `Renewed through ${membershipRenewalDueLabel(entry.renewal_due_on_after)}`,
      entry,
    )
  }

  if (entry.event === 'Level changed') return levelChangedDescription(entry)
  if (entry.event === 'Dates corrected') return dateCorrectionDescription(entry)

  return withPaymentDetail(entry.event, entry)
}

function withPaymentDetail(description: string, entry: ProfileMembershipHistory) {
  const payment = entry.payment_handling?.toLowerCase()
  if (!payment) return description

  return `${description} - payment handled ${payment}`
}

function levelChangedDescription(entry: ProfileMembershipHistory) {
  const before = entry.membership_level_before ?? 'previous level'
  const after = entry.membership_level_after ?? 'new level'

  return `Level changed from ${before} to ${after}`
}

function dateCorrectionDescription(entry: ProfileMembershipHistory) {
  const changes = []
  if (entry.member_since_on_before || entry.member_since_on_after) {
    changes.push(
      `member since ${displayDate(entry.member_since_on_before)} to ${displayDate(
        entry.member_since_on_after,
      )}`,
    )
  }
  if (entry.renewal_due_on_before || entry.renewal_due_on_after) {
    changes.push(
      `renewal due ${membershipRenewalDueLabel(
        entry.renewal_due_on_before,
      )} to ${membershipRenewalDueLabel(entry.renewal_due_on_after)}`,
    )
  }

  return changes.length > 0 ? `Dates corrected: ${changes.join(', ')}` : 'Dates corrected'
}
